import React, { createContext, useContext, useState, ReactNode, useCallback, useRef } from 'react';
import './notification.css';
import { useToastContext } from '../context/ToastContext';

type NotificationType = 'success' | 'error' | 'warning' | 'info' | 'confirm';

interface NotificationRequest {
  type: NotificationType;
  title?: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  confirmVariant?: 'primary' | 'danger';
  duration?: number;
}

interface ConfirmState {
  isOpen: boolean;
  title: string;
  description: string;
  confirmLabel: string;
  cancelLabel: string;
  confirmVariant: 'primary' | 'danger';
}

interface NotificationContextType {
  notify: (config: NotificationRequest) => Promise<boolean> | void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const NotificationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const toast = useToastContext();
  const [confirmState, setConfirmState] = useState<ConfirmState | null>(null);
  const confirmResolver = useRef<((value: boolean) => void) | null>(null);

  const closeConfirm = useCallback((result: boolean) => {
    confirmResolver.current?.(result);
    confirmResolver.current = null;
    setConfirmState(null);
  }, []);

  const notify = useCallback((config: NotificationRequest) => {
    if (config.type === 'confirm') {
      return new Promise<boolean>((resolve) => {
        confirmResolver.current = resolve;
        setConfirmState({
          isOpen: true,
          title: config.title || 'Emin misiniz?',
          description: config.message,
          confirmLabel: config.confirmText || 'Onayla',
          cancelLabel: config.cancelText || 'Vazgeç',
          confirmVariant: config.confirmVariant || 'danger'
        });
      });
    }

    switch (config.type) {
      case 'success':
        toast.success(config.message, config.duration);
        break;
      case 'error':
        toast.error(config.message, config.duration);
        break;
      case 'warning':
        toast.warning(config.message, config.duration);
        break;
      default:
        toast.info(config.message, config.duration);
        break;
    }

    return;
  }, [toast]);

  const handleConfirm = useCallback(() => {
    closeConfirm(true);
  }, [closeConfirm]);

  const handleCancel = useCallback(() => {
    closeConfirm(false);
  }, [closeConfirm]);

  return (
    <NotificationContext.Provider value={{ notify }}>
      {children}
      {confirmState && confirmState.isOpen && (
        <div className="notification-overlay">
          <div className="notification-modal">
            <div className="notification-header">
              <div className={`notification-icon ${confirmState.confirmVariant === 'danger' ? 'destructive' : ''}`}>
                !
              </div>
              <div>
                <h3>{confirmState.title}</h3>
                <p>{confirmState.description}</p>
              </div>
            </div>
            <div className="notification-actions">
              <button className="notification-cancel" onClick={handleCancel}>
                {confirmState.cancelLabel}
              </button>
              <button
                className={`notification-confirm ${confirmState.confirmVariant === 'danger' ? 'destructive' : ''}`}
                onClick={handleConfirm}
              >
                {confirmState.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </NotificationContext.Provider>
  );
};

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within NotificationProvider');
  }
  return context;
};